import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import IcErrorFilled from '@deliveryhero/pd-cookbook/components/Icons/IcErrorFilled';
import IcSuccessFilled from '@deliveryhero/pd-cookbook/components/Icons/IcSuccessFilled';
import { Link } from '@deliveryhero/pd-cookbook/components/Link';
import {
  Alert,
  AlertColor,
  Box,
  Container,
  Grid,
  Typography
} from '@mui/material';

import AuthFooter from 'components/AuthFooter';
import { ForgotPasswordForm } from 'components/Forms/ForgotPasswordForm';
import { ForgotPasswordFormValues } from 'components/Forms/ForgotPasswordForm/validation';
import { Logo } from 'components/Logo';
import { BrandCodes } from 'components/Theme/types';
import { useAuth } from 'contexts/authentication';
import { useConfiguration } from 'contexts/country-config';
import useNotification from 'hooks/useNotification';

const PAGE_TITLE = [
  'Reset your password',
  'Check your email for instructions to reset your password'
];
const PAGE_SUBTITLE = [
  "We'll send a password reset link to your email.",
  "Didn't get the email? Check your spam folder or request for a"
];

const ALERT_COLOR: AlertColor[] = ['info', 'error', 'warning', 'success'];

export default function ForgotPassword() {
  const auth = useAuth();
  const countryConfig = useConfiguration();
  const brandCode = countryConfig.entity.code.toLowerCase() as BrandCodes;

  const [resetPassword, setResetPassword] = useState<string | undefined>(
    undefined
  );
  const [searchParams] = useSearchParams();
  const [alertType] = useState<AlertColor>(
    (searchParams.get('type') as AlertColor) || 'error'
  );
  const [alertMessage, setAlertMessage] = useState<string>(
    searchParams.get('message') || ''
  );

  const { pushNotification } = useNotification();

  const handleSubmit = async (
    values: ForgotPasswordFormValues,
    resend = false
  ) => {
    return (
      auth.forgotPassword &&
      auth
        .forgotPassword(values.username)
        .then((res: { data: { success: boolean } }) => {
          res.data.success && setResetPassword(values.username);
          resend &&
            pushNotification('Email sent successfully', {
              variant: 'success'
            });
        })
        .catch((e: Error) => {
          setAlertMessage(e.message);
        })
    );
  };

  const initialValues = {
    username: ''
  };

  return (
    <Container disableGutters maxWidth={false}>
      <div
        style={{
          position: 'absolute',
          bottom: '-1%'
        }}
      >
        <img
          src={`https://images.deliveryhero.io/image/fd-sg/corporate/company-admin/Rectangle%20Diagonal.png`}
          style={{
            width: '38%'
          }}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '8%',
          pointerEvents: 'none'
        }}
      >
        <img
          src={`https://images.deliveryhero.io/image/fd-sg/corporate/company-admin/Login%20experience%20Email.png`}
          style={{
            width: '31%'
          }}
        />
      </div>
      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: '100px',
          pl: '390px'
        }}
      >
        <Box
          component="div"
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="400px"
        >
          <Logo brandCode={brandCode} style={{ paddingBottom: '16px' }} />
          <Typography
            sx={{
              color: '#333333',
              fontWeight: '700',
              fontSize: resetPassword ? '18px' : '32px',
              lineHeight: resetPassword ? '27px' : '40px',
              textAlign: 'center'
            }}
            pb="16px"
          >
            {resetPassword ? PAGE_TITLE[1] : PAGE_TITLE[0]}
          </Typography>
          <Typography
            sx={{
              color: '#333333',
              fontWeight: '300',
              fontSize: '14px',
              lineHeight: '21px',
              textAlign: 'center'
            }}
            pb="16px"
          >
            {resetPassword ? PAGE_SUBTITLE[1] : PAGE_SUBTITLE[0]}
            {resetPassword && (
              <>
                <br></br>
                <Link
                  href=""
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    handleSubmit({ username: resetPassword }, true)
                  }
                >
                  resend
                </Link>
                .
              </>
            )}
          </Typography>
          {Boolean(alertMessage) && (
            <Alert
              severity={
                (ALERT_COLOR.some((type) => type === alertType) && alertType) ||
                'error'
              }
              iconMapping={{
                error: <IcErrorFilled />,
                success: <IcSuccessFilled style={{ fill: '#3C8264' }} />
              }}
              sx={{
                mb: '16px',
                width: '100%',
                boxSizing: 'border-box',
                fill: '#E03000'
              }}
            >
              {alertMessage}
            </Alert>
          )}
          {!resetPassword && (
            <ForgotPasswordForm
              initialValues={initialValues}
              onSubmit={handleSubmit}
            ></ForgotPasswordForm>
          )}
        </Box>
        <AuthFooter
          contactEmail={countryConfig.contact_us}
          indexUrl={countryConfig.index}
          brand={countryConfig.entity.brand.name}
        />
      </Grid>
    </Container>
  );
}
